import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { BaseComponent, WmsToggleComponent, WmsMessageComponent, WmsTypeaheadComponent } from '@wms/app/components';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { SecurityService, DatabaseComponent } from '@wms/settings/security';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { forEach, includes, isEqual } from 'lodash-es';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'wms-cas',
  templateUrl: './cas.component.html',
  styleUrls: ['./cas.component.less'],
  imports: [CommonModule, FormsModule, WmsToggleComponent, DatabaseComponent, WmsMessageComponent, WmsTypeaheadComponent, TooltipModule]
})
export class CasComponent extends BaseComponent implements OnInit {
  @Input() securityProps: any;
  @Input() authInfo: any;
  securityService: SecurityService;
  security: any;
  hideMessage: boolean = true;
  message: any;
  statusClass: any;
  iconClass: any;
  statusMessage: any;
  testCasAttrs = ['loginUrl', 'validationUrl', 'serviceParameter', 'artifactParameter'];
  testCasSuccess: any;
  formChangesSubscription: Subscription;
  resetFormSubscription: Subscription;
  @ViewChild('casForm', { static: true }) ngForm: NgForm;

  // private httpService: WmHttpService
  constructor() {
    super();
    this.securityService = inject(SecurityService);
  }

  ngOnInit(): void {
    this.security = this.securityService.security;
    this.security.userCASRoleProviders = ['CAS', 'Database'];
    if (this.security.enableSecurity) {
      //hiding error messages if any, since provider was changed
      this.hideMessage = true;
    }
    this.security.showUnamePass = false;
    // TODO
    // this.handleWatchers(provider);
    this.security.serverUrl = '';
    let setInitialValue: boolean = true;
    if (this.securityService.casFormInitialValue) {
      setInitialValue = false;
      setTimeout(() => {
        this.ngForm.form.patchValue(this.securityService.casFormInitialValue);
      });
    }

    this.formChangesSubscription = this.ngForm.form.valueChanges.subscribe(val => {
      if (!this.ngForm.form.dirty && setInitialValue) {
        this.securityService.casFormInitialValue = this.ngForm.form.value;
      }
      if (this.ngForm.form.dirty) {
        if (isEqual(this.securityService.casFormInitialValue, this.ngForm.form.value)) {
          this.security.dirtyCheckList.splice(this.security.dirtyCheckList.indexOf('casProvider', 1));
        } else if (!includes(this.security.dirtyCheckList, 'casProvider')) {
          this.security.dirtyCheckList.push('casProvider');
        }
      }
    });


    this.resetFormSubscription = this.securityService.resetForm.subscribe((resetInitialVal) => {
      if (!resetInitialVal) {
        this.ngForm.form.reset(this.securityService.casFormInitialValue);
      } else {
        this.securityService.casFormInitialValue = this.ngForm.form.value;
      }
    });
    this.securityService.getCASOptions(this.authInfo);
  }

  updateFields(oldValue: any, property: any, relativeUrl: any) {
    if (!this.security[property]) {
      this.security[property] = this.security.serverUrl + relativeUrl;
    } else if (oldValue === this.security[property].substr(0, oldValue.length)) {
      if (!this.security.serverUrl) {
        this.security[property] = '';
      } else {
        this.security[property] = this.security[property].replace(oldValue, this.security.serverUrl);
      }
    }
  }

  //Autopopulate the login,validation urls based on the server url
  populateUrls(oldValue: any) {
    this.updateFields(oldValue, 'loginUrl', '/login');
    this.updateFields(oldValue, 'logoutUrl', '/logout');
    this.updateFields(oldValue, 'validationUrl', '');
    // clear roleAttributes on server Url change.
    this.security.attributes = [];
    this.security.roleAttributeName = '';
  }

  setStatus(type: any) {
    switch (type) {
      case 'success':
        this.statusClass = 'text-success';
        this.iconClass = 'wms-done-bold';
        this.statusMessage = this.locale.MESSAGE_CONNECTION_SUCCESSFUL;
        break;
      case 'info':
        this.statusClass = 'text-info';
        this.iconClass = 'wms-widget-spinner fa-spin';
        this.statusMessage = this.locale.MESSAGE_TESTING_PROGRESS;
        break;
      case 'error':
        this.statusClass = 'text-danger';
        this.iconClass = 'wms-highlight-off';
        this.statusMessage = this.locale.MESSAGE_CONNECTION_ERROR;
        break;
    }
  }

  handleModelChange($event: any) {
    this.security.roleAttributeName = $event.value;
  }

  //Constructing the cas test connection url
  constructTestUrl() {
    let testUrl,
      queryString = '',
      value;
    testUrl = '/studio/services/securityservice/providers/cas/testconnection';
    forEach(this.testCasAttrs, (attr) => {
      value = attr + '=' + this.security[attr];
      queryString += value + '&';
    });
    testUrl += '?' + queryString;
    return testUrl;
  }

  casLoginSuccess(e: any) {
    if (e.data) {
      this.security.attributes = Object.keys(e.data?.attributes);
      this.testCasSuccess = true;
    }
  }

  testCasConnection() {
    let testUrl,
      id: any,
      casLoginPopup: any,
      countInSeconds = 0,
      ONE_SEC = 1000,
      timeInterval = 2 * ONE_SEC,     //2 seconds
      timeoutValue = 5 * 60 * ONE_SEC, //5 mintues
      vh = 565,
      vw = 940;

    this.hideMessage = true;

    if (!this.security.enableSecurity) {
      return;
    }

    if (!this.security.serverUrl || this.security.serverUrl === '') {
      this.hideMessage = false;
      this.message = {
        type: 'error',
        caption: this.locale.MESSAGE_CAS_URL_NOT_SET
      }
      return;
    } else if (!this.security.loginUrl || this.security.loginUrl === '') {
      this.hideMessage = false;
      this.message = {
        type: 'error',
        caption: this.locale.MESSAGE_CAS_LOGIN_URL_NOT_SET
      }
      return;
    } else if (!this.security.validationUrl || this.security.validationUrl === '') {
      this.hideMessage = false;
      this.message = {
        type: 'error',
        caption: this.locale.MESSAGE_CAS_VALIDATION_URL_NOT_SET
      }
      return;
    }
    testUrl = this.constructTestUrl();
    casLoginPopup = window.open(testUrl, 'CAS Login', 'height=' + vh + 'px, width=' + vw + 'px, left=' + vw / 3 + 'px, top=' + vh * 0.12 + 'px');
    this.testCasSuccess = false;
    this.setStatus('info');
    // Listening to the message received from the CAS Login window
    window.addEventListener('message', this.casLoginSuccess.bind(this));
    //Check for the cookie for certain interval of time
    id = setInterval(() => {
      countInSeconds += 2;
      if (this.testCasSuccess) {
        clearInterval(id);
        this.setStatus('success');
        window.removeEventListener('message', this.casLoginSuccess);
      } else if (countInSeconds > timeoutValue || !casLoginPopup.self) {
        // Show error status if popup window is closed without login.
        clearInterval(id);

        this.setStatus('error');
      }
    }, timeInterval);
  }

  override ngOnDestroy(): void {
    this.formChangesSubscription?.unsubscribe();
    this.resetFormSubscription?.unsubscribe();
  }
}


